import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { isEqual } from "lodash";
import classNames from "classnames";

// UI Components
import BootstrapTable from "react-bootstrap-table-next"; // https://react-bootstrap-table.github.io/react-bootstrap-table2/storybook/index.html
import paginationFactory from "react-bootstrap-table2-paginator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { toastr } from "react-redux-toastr";
import { Row, Col, Button, Container, Card, CardBody } from "reactstrap";

// State
import {
  getUsers,
  getApprovers,
  createApprover,
  selectApprover,
  updateApprover,
  deleteApprover,
} from "../../../redux/actions/adminActions";

// Custom Components
import { Group } from "../../../utils/logger";
import Loader from "../../../components/Loader";
import Header from "./Header";
import approver_table_schema from "./approverTableSchema";
import ApproverModal from "./ApproverModal";

// Globals
const RECORDS_PER_PAGE = "mbEonsRecordsPerPage";

class Approvers extends Component {
  constructor(props) {
    super(props);

    this.state = {
      search_text: "",
      approvers: props.approvers,
      selected: props.selected,
      recordsPerPage: localStorage.getItem(RECORDS_PER_PAGE) || 50,

      // Modal
      modal: {
        type: "add",
        isOpen: false,
        applyChanges: this.addUser,
      },
    };

    this.LoggerGroup = new Group("Approvers", { disable: true });

    this.toggleModal = this.toggleModal.bind(this);
    this.onUserAction = this.onUserAction.bind(this);
    this.onSearchApprovers = this.onSearchApprovers.bind(this);
  }

  componentDidMount() {
    this.LoggerGroup.emit("Mounted").end();

    this.props.getUsers();
    this.props.getApprovers();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const state = {};

    if (!isEqual(nextProps.approvers, prevState.approvers)) {
      state.approvers = nextProps.approvers;
    }
    if (!isEqual(nextProps.selected, prevState.selected)) {
      state.selected = nextProps.selected;
    }

    return state;
  }

  onSearchApprovers({ target }) {
    // this.LoggerGroup.emit("Searching value", target.name, target.value);

    this.setState({ search_text: target.value });
  }

  onUserAction(type, approver) {
    this.LoggerGroup.emit("User Action triggered", { dir: approver });

    switch (type) {
      case "add":
        this.LoggerGroup.emit(`  [i] Adding User`);
        this.props.selectApprover({});
        this.toggleModal(true, this.props.createApprover);
        break;

      case "edit":
        this.LoggerGroup.emit(`  [i] Editing User`);
        this.props.selectApprover(approver);
        this.toggleModal(true, this.props.updateApprover);
        break;

      case "delete":
        this.props.selectApprover(approver);
        this.LoggerGroup.emit(`  [i] Deleting User`);
        toastr.confirm("Are you sure you want to remove this user as an approver?", {
          okText: "Delete Approver",
          onCancel: () =>
            this.LoggerGroup.emit(`Cancelled delete approver[${approver.approverId_bint}]`),
          onOk: () => this.props.deleteApprover(approver),
        });
        break;

      default:
        return this.LoggerGroup.emit({ error: `Invalid user action type[${type}]` });
    }

    this.LoggerGroup.end();
  }

  toggleModal(show, applyChanges) {
    const { modal } = this.state;
    modal.isOpen = typeof show === "boolean" ? show : !modal.isOpen;
    modal.applyChanges = applyChanges;

    this.setState({ modal });
  }

  render() {
    const { onUserAction } = this;
    const { loading, users, thisUser } = this.props;
    const { search_text, approvers, selected, modal, recordsPerPage } = this.state;

    if (loading) {
      return <Loader />;
    }

    const search_text_regex = new RegExp(search_text, "i");
    const filtered_approvers = approvers.filter(
      (approver) =>
        search_text_regex.test(approver.firstname_vch) ||
        search_text_regex.test(approver.lastname_vch)
    );

    const AddApprover = (props) => (
      <Row className={classNames("mb-4", props.className)}>
        <Col className="text-right">
          <Button color="primary" onClick={() => onUserAction("add")}>
            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
            Add Approver
          </Button>
        </Col>
      </Row>
    );

    return (
      <Container fluid className="p-0">
        <Header onSearchApprovers={this.onSearchApprovers} />

        {modal.isOpen && (
          <ApproverModal
            {...modal}
            users={users.filter(
              (user) => !approvers.find((approver) => user.user_id_bint === approver.userId_bint)
            )}
            thisUser={thisUser}
            selected={selected}
            toggleModal={this.toggleModal}
          />
        )}

        <AddApprover />

        <Card>
          <CardBody>
            <p>
              The following approvers, when selected, will receive an email and/or text message to
              their provided device(s) with instructions on how to approve a campaign.
            </p>
            <p>
              A campaign may have multiple approvers selected, but only one approval is needed to
              send the campaign
            </p>
          </CardBody>

          <CardBody>
            <BootstrapTable
              bootstrap4
              className="table-responsive"
              hover
              keyField="approverId_bint"
              data={filtered_approvers}
              columns={approver_table_schema({ onUserAction })}
              bordered={false}
              pagination={paginationFactory({
                sizePerPage: Number(recordsPerPage),
                sizePerPageList: [5, 10, 25, 50, 75, 100],
                onSizePerPageChange: (sizePerPage) => {
                  localStorage.setItem(RECORDS_PER_PAGE, sizePerPage);
                },
              })}
              defaultSorted={[
                {
                  dataField: "firstname_vch",
                  order: "asc",
                },
              ]}
              rowClasses={(approver) => {
                return approver.approverId_bint === selected.approverId_bint ? "table-info" : null;
              }}
            />
          </CardBody>
        </Card>

        {filtered_approvers.length > 5 && <AddApprover className="d-lg-none" />}
      </Container>
    );
  }
}

Approvers.propTypes = {
  errors: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  users: PropTypes.array.isRequired,
  thisUser: PropTypes.object.isRequired,
  approvers: PropTypes.array.isRequired,
  selected: PropTypes.object.isRequired,
  getUsers: PropTypes.func.isRequired,
  getApprovers: PropTypes.func.isRequired,
  createApprover: PropTypes.func.isRequired,
  selectApprover: PropTypes.func.isRequired,
  updateApprover: PropTypes.func.isRequired,
  deleteApprover: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  errors: state.campaigns.errors,
  users: state.admin.users,
  thisUser: state.user.user,
  loading: state.admin.loading.approvers,
  approvers: state.admin.approvers,
  selected: state.admin.selectedApprover,
});

export default connect(mapStateToProps, {
  getUsers,
  getApprovers,
  createApprover,
  selectApprover,
  updateApprover,
  deleteApprover,
})(Approvers);
